import { 
  auth, 
  db, 
  storage,
  Timestamp, 
  serverTimestamp, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  deleteDoc,
  writeBatch,
  storageRef,
  deleteObject as deleteFileStorage, // Renamed to avoid conflict with deleteDoc
} from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import type { Agent, UserListingData, Like, Chat, DashboardListing, DashboardMatch } from './types';

// --- Agent & Auth ---

interface AgentRegistrationDetails {
  email: string;
  companyName: string;
  contactPerson: string;
  phone: string;
}

export const registerAgent = async (details: AgentRegistrationDetails, password: string): Promise<void> => {
  const userCredential = await createUserWithEmailAndPassword(auth, details.email, password);
  const user = userCredential.user;

  const agentData: Omit<Agent, 'uid'> = {
    email: details.email,
    companyName: details.companyName,
    contactPerson: details.contactPerson,
    phone: details.phone,
    isApproved: false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(db, 'agents', user.uid), agentData);
};

export const loginAgent = async (email: string, password: string): Promise<void> => {
  await signInWithEmailAndPassword(auth, email, password);
};

export const getAgentData = async (uid: string): Promise<Agent | null> => {
  const agentDocRef = doc(db, 'agents', uid);
  const docSnap = await getDoc(agentDocRef);
  if (docSnap.exists()) {
    return { uid, ...docSnap.data() } as Agent;
  }
  return null;
};

// --- Listings ---

export const addListing = async (listingData: UserListingData): Promise<string> => {
  const newListingRef = doc(collection(db, 'users'));
  const fullListingData = {
    ...listingData,
    uid: newListingRef.id, 
    createdAt: serverTimestamp(), 
    updatedAt: serverTimestamp(),
  };
  await setDoc(newListingRef, fullListingData);
  return newListingRef.id;
};

export const updateListing = async (listingId: string, listingData: Partial<UserListingData>): Promise<void> => {
  const listingDocRef = doc(db, 'users', listingId);
  const dataToUpdate = {
    ...listingData,
    updatedAt: serverTimestamp(),
  };
  delete dataToUpdate.createdAt; 
  await updateDoc(listingDocRef, dataToUpdate);
};


export const getListingsForAgent = async (agentUid: string): Promise<UserListingData[]> => {
  const q = query(collection(db, 'users'), where('agentUid', '==', agentUid));
  const querySnapshot = await getDocs(q);
  const listings: UserListingData[] = [];
  querySnapshot.forEach((doc) => {
    listings.push({ uid: doc.id, ...doc.data() } as UserListingData);
  });
  return listings;
};

export const getListingById = async (listingId: string): Promise<UserListingData | null> => {
  const docRef = doc(db, 'users', listingId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { uid: docSnap.id, ...docSnap.data() } as UserListingData;
  }
  return null;
};

export const deleteListing = async (listingId: string, imageUrls: string[] = []): Promise<void> => {
  const batch = writeBatch(db);
  
  const listingDocRef = doc(db, "users", listingId);
  batch.delete(listingDocRef);

  // De app schrijft likes als { userId, likedUserId } — het listing-doc is de likedUserId
  const likesQuery = query(collection(db, "likes"), where("likedUserId", "==", listingId));
  const likesSnapshot = await getDocs(likesQuery);
  likesSnapshot.forEach(likeDoc => {
    batch.delete(likeDoc.ref);
  });
  
  await batch.commit();

  for (const imageUrl of imageUrls) {
    try {
      if (imageUrl.includes('firebasestorage.googleapis.com')) { 
        const imageRef = storageRef(storage, imageUrl); 
        await deleteFileStorage(imageRef);
      }
    } catch (error: any) {
      console.warn(`Failed to delete image ${imageUrl} from storage:`, error.message);
    }
  }
};


// --- Likes ---

export const getLikesForListing = async (listingId: string): Promise<Like[]> => {
  const q = query(collection(db, 'likes'), where('likedUserId', '==', listingId));
  const querySnapshot = await getDocs(q);
  const likes: Like[] = [];
  querySnapshot.forEach((doc) => {
    likes.push({ id: doc.id, ...doc.data() } as Like);
  });
  return likes;
};

// --- Chats ---

// getChatsForListing() is bewust verwijderd. Die las per match `chats/{matchId}`,
// maar dat document bestaat niet: berichten leven uitsluitend in de subcollectie
// `chats/{matchId}/messages`. Of er een gesprek loopt lezen we nu gratis af aan
// `lastMessage` op het match-document zelf (zie toDashboardMatch hieronder).

export const initiateChat = async (agentUid: string, searchingUserUid: string, listingId: string): Promise<string> => {
  // Create a predictable, ordered chat ID
  const participantsArray = [agentUid, searchingUserUid].sort();
  const chatId = `${participantsArray[0]}_${participantsArray[1]}_${listingId}`;
  
  const chatDocRef = doc(db, 'chats', chatId);
  const chatDocSnap = await getDoc(chatDocRef);

  if (!chatDocSnap.exists()) {
    const newChatData: Chat = {
      participants: participantsArray,
      listingId: listingId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      // lastMessage can be updated via a cloud function or when a new message is sent
    };
    await setDoc(chatDocRef, newChatData);
  } else {
    // Optionally update updatedAt timestamp if chat already exists and is being "re-opened"
    await updateDoc(chatDocRef, { updatedAt: Timestamp.now() });
  }
  
  return chatId;
};

export const getUserById = async (uid: string): Promise<any> => {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { uid: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const getMatchesForListing = async (listingId: string): Promise<any[]> => {
  // We nemen aan dat listingId de uid is van de agent-listing user
  const q = query(collection(db, 'matches'), where('users', 'array-contains', listingId), where('status', '==', 'active'));
  const querySnapshot = await getDocs(q);
  const matches: any[] = [];
  querySnapshot.forEach((doc) => {
    matches.push({ id: doc.id, ...doc.data() });
  });
  return matches;
};

// --- Dashboard ---

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
};

/** Zet een ruw match-document om naar de vorm die het dashboard nodig heeft. */
const toDashboardMatch = (matchId: string, data: any, listingId: string): DashboardMatch | null => {
  const otherUserId = (data.users || []).find((uid: string) => uid !== listingId);
  if (!otherUserId) return null;

  const details = data.userDetails?.[otherUserId] || {};
  const lastMessage: string | null = data.lastMessage || null;

  return {
    id: matchId,
    listingId,
    otherUserId,
    name: details.firstName || 'Onbekende gebruiker',
    profileImage: details.profileImage || null,
    lastMessage,
    lastMessageAt: toDate(data.lastMessageTimestamp),
    createdAt: toDate(data.createdAt),
    // Of er een gesprek loopt staat al op het match-document. Vroeger werd hiervoor
    // per match de hele berichten-subcollectie opgehaald; dat is nu overbodig.
    hasChat: Boolean(lastMessage),
    awaitingReply: Boolean(lastMessage) && data.lastMessageSenderId === otherUserId,
  };
};

/**
 * Haalt in één ronde alles op wat het dashboard toont: de woningen van de makelaar
 * en per woning de actieve matches. Alle queries lopen parallel, dus de laadtijd is
 * één netwerkronde in plaats van ~145 opeenvolgende verzoeken.
 */
export const getDashboardData = async (agentUid: string): Promise<DashboardListing[]> => {
  const listings = await getListingsForAgent(agentUid);

  const withMatches = await Promise.all(
    listings.map(async (listing): Promise<DashboardListing> => {
      if (!listing.uid) return { listing, matches: [] };

      const snapshot = await getDocs(query(
        collection(db, 'matches'),
        where('users', 'array-contains', listing.uid),
        where('status', '==', 'active'),
      ));

      const matches = snapshot.docs
        .map(d => toDashboardMatch(d.id, d.data(), listing.uid as string))
        .filter((m): m is DashboardMatch => m !== null)
        .sort((a, b) => {
          // Reacties die op antwoord wachten eerst, daarna op recentheid.
          if (a.awaitingReply !== b.awaitingReply) return a.awaitingReply ? -1 : 1;
          const at = (a.lastMessageAt || a.createdAt)?.getTime() || 0;
          const bt = (b.lastMessageAt || b.createdAt)?.getTime() || 0;
          return bt - at;
        });

      return { listing, matches };
    }),
  );

  return withMatches;
};
